package com.kinoshazam.screens.main.compact

import androidx.compose.foundation.Image
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DrawerValue
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ModalDrawerSheet
import androidx.compose.material3.ModalNavigationDrawer
import androidx.compose.material3.Scaffold
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.material3.rememberDrawerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.unit.dp
import com.kinoshazam.screens.main.DecideToolbarIcon
import com.kinoshazam.uiKit.animations.scaleAnimation
import com.kinoshazam.uiKit.components.textLabel.TextLabel
import com.kinoshazam.uiKit.components.textLabel.TextLabelState
import com.kinoshazam.uiKit.components.toStringResource
import com.kinoshazam.uiKit.extension.thenIf
import com.kinoshazam.uiKit.theme.BoldLarge
import com.kinoshazam.uiKit.theme.UiKitTheme
import com.kinoshazam.view.DrawerContent
import com.kinoshazam.view.ErrorView
import com.kinoshazam.view.HandleDrawer
import com.kinoshazam.view.OnBackPressed
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.error
import kinoshazam.resources.generated.resources.loading
import kinoshazam.resources.generated.resources.menu
import kinoshazam.resources.generated.resources.repeat
import kinoshazam.resources.generated.resources.search
import kinoshazam.resources.generated.resources.update
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import presentation.screens.main.MainMVIAction
import presentation.screens.main.MainState
import presentation.screens.main.MainState.DrawerStateValue
import presentation.screens.main.MainState.MenuItem
import presentation.screens.main.MainState.MenuItem.CARTOONS
import presentation.screens.main.MainState.MenuItem.FAVORITES
import presentation.screens.main.MainState.MenuItem.KINOSHKA

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CompactMainWindow(
    state: MainState,
    onAction: (MainMVIAction) -> Unit
) {
    val scope = rememberCoroutineScope()

    OnBackPressed(
        enabled = state.selectedMenu != MenuItem.KINOSHKA ||
                state.drawerState != DrawerStateValue.CLOSED
    ) {
        when {
            state.drawerState != DrawerStateValue.CLOSED -> {
                onAction(MainMVIAction.ClickBackWithDrawer)
            }

            state.selectedMenu != MenuItem.KINOSHKA -> {
                onAction(MainMVIAction.OnMenuItemClicked(MenuItem.KINOSHKA))
            }
        }
    }

    val drawerState = rememberDrawerState(initialValue = DrawerValue.Closed)

    HandleDrawer(
        drawerState = drawerState,
        state = state,
        onAction = onAction,
    )
    ModalNavigationDrawer(
        drawerState = drawerState,
        drawerContent = {
            ModalDrawerSheet(
                drawerContainerColor = UiKitTheme.colors.color_3
            ) {
                DrawerContent(drawerState, state) { event ->
                    scope.launch {
                        drawerState.close()
                    }
                    onAction(event)
                }
            }
        }
    ) {
        Scaffold(
            topBar = {
                TopAppBar(
                    title = {
                        TextLabel(
                            state = TextLabelState(
                                text = state.selectedMenu.title.toStringResource()
                                    ?.let { stringResource(it) }
                                    ?: state.selectedMenu.title,
                                color = UiKitTheme.colors.color_2,
                                textStyle = BoldLarge
                            )
                        )
                    },
                    navigationIcon = {
                        Image(
                            painter = painterResource(
                                if (state.updateAvailable) { // TODO: why always updating???
                                    Res.drawable.update
                                } else {
                                    Res.drawable.menu
                                }
                            ),
                            colorFilter = ColorFilter.tint(UiKitTheme.colors.color_1),
                            contentDescription = null,
                            modifier = Modifier
                                .padding(horizontal = 16.dp)
                                .clickable {
                                    onAction(MainMVIAction.OnMenuClicked)
                                }
                                .thenIf(Modifier.scale(scaleAnimation()), state.updateAvailable)
                        )
                    },
                    actions = {
                        DecideToolbarIcon(
                            visible = state.tasteEnabled,
                            onClick = { onAction(MainMVIAction.DecideClicked) },
                        )
                        Image(
                            painter = painterResource(Res.drawable.search),
                            colorFilter = ColorFilter.tint(UiKitTheme.colors.color_1),
                            contentDescription = null,
                            modifier = Modifier
                                .padding(end = 16.dp)
                                .clickable {
                                    onAction(MainMVIAction.OnSearchClicked)
                                }
                        )
                    },
                    colors = TopAppBarDefaults.topAppBarColors(
                        containerColor = UiKitTheme.colors.color_3,
                    ),
                )
            },
            containerColor = UiKitTheme.colors.color_3,
            content = { paddingValues ->
                when {
                    state.selectedMenu == KINOSHKA && state.isMainError -> ErrorScreen(onAction = onAction)
                    state.selectedMenu == FAVORITES && state.isFavoriteError -> ErrorScreen(onAction = onAction)
                    state.selectedMenu == CARTOONS && state.isCartoonError -> ErrorScreen(onAction = onAction)
                    state.isError -> ErrorScreen(onAction = onAction)
                    else -> MainContentScreen(
                        paddingValues = paddingValues,
                        state = state,
                        onAction = onAction
                    )
                }
            },
        )
    }
}

@Composable
fun MainContentScreen(
    paddingValues: PaddingValues,
    state: MainState,
    onAction: (MainMVIAction) -> Unit,
) {

    when (state.selectedMenu) {
        KINOSHKA -> MainContent(paddingValues, state = state, onAction)

        MenuItem.FILMS -> MoviesContent(
            paddingValues = paddingValues,
            state = state,
            onAction = onAction,
        )

        MenuItem.TVSHOWS -> TvShowsContent(
            paddingValues = paddingValues,
            state = state,
            onAction = onAction
        )

        CARTOONS -> CartoonContent(
            paddingValues = paddingValues,
            state = state,
            onAction = onAction
        )

        FAVORITES -> {
//            onAction(MainMVIAction.FavoriteOpened)
            FavoriteContent(
                paddingValues = paddingValues,
                state = state,
                onAction = onAction
            )
        }
    }
}

@Composable
private fun LoadingScreen(text: String = stringResource(Res.string.loading)) {
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center,
    ) {
        CircularProgressIndicator(
            modifier = Modifier.size(24.dp),
            color = UiKitTheme.colors.color_1,
            strokeWidth = 2.dp,
        )
//        TextLabel(
//            state = TextLabelState(
//                text = text,
//                textStyle = Medium,
//                color = UiKitTheme.colors.color_2
//            ),
//            modifier = Modifier.align(Alignment.Center)
//        )
    }
}

@Composable
private fun ErrorScreen(onAction: (MainMVIAction) -> Unit) {
    ErrorView(
        textError = stringResource(Res.string.error),
        textAction = stringResource(Res.string.repeat),
        onAction = { onAction(MainMVIAction.Reload) }
    )
}